import type { CaptureStorageLevel } from '@sb/feathers-types';

import type { PruneOutcome } from './pruneCaptureDirectory';

/**
 * How full a capture directory has to be before an operator is told.
 *
 * Far enough below the bound that there is still time to act. The warning
 * exists so records can be copied off the robot before anything is deleted, and
 * one sent at 95% would arrive with minutes of inspecting left to spare.
 */
export const CAPTURE_STORAGE_WARNING_FRACTION = 0.75;

/** The reason pruning gives when it deleted to stay inside the size bound. */
const ROTATED_REASON = 'too-large';

/** What the capture path hands to whatever delivers the warning. */
export interface CaptureStorageReport {
  /** The directory measured, which is one routine and skill's. */
  directory: string;
  level: CaptureStorageLevel;
  /** What the directory holds after pruning. */
  usedBytes: number;
  /** What it may hold. */
  limitBytes: number;
  skillId?: string;
  routineId?: string;
}

/**
 * How close one capture directory is to its bound, or `null` while it is fine.
 *
 * `rotating` is decided by what pruning DID rather than by the usage figure,
 * because after a prune the directory is by definition back under the bound —
 * usage alone can never show that a capture was deleted, which is the thing
 * worth saying.
 */
export function classifyCaptureStorage(args: {
  outcome: PruneOutcome;
  limitBytes: number;
}): CaptureStorageLevel | null {
  const { outcome, limitBytes } = args;

  if (outcome.reasons.includes(ROTATED_REASON)) {
    return 'rotating';
  }

  // A limit of zero is a real configuration — "keep nothing" — and there is no
  // fraction of it to be near. It reports as rotating above whenever it deletes
  // anything, which is the honest description of that setting.
  if (limitBytes <= 0) {
    return null;
  }

  if (outcome.usedBytes >= limitBytes * CAPTURE_STORAGE_WARNING_FRACTION) {
    return 'warning';
  }

  return null;
}

/**
 * Decides when a level is worth sending, given what was last sent.
 *
 * Pruning runs after EVERY capture, so a robot sitting at 80% would otherwise
 * raise a warning per frame — hundreds an hour, each one identical, which is
 * how a notification queue becomes something operators stop reading.
 *
 * Reported only when the pressure passes its HIGH-WATER mark. A directory at
 * its bound alternates between the two levels — a capture that prunes reports
 * `rotating`, the next fits in the headroom that prune made and reports
 * `warning` — so comparing against the previous level alone notified on every
 * swing, which is the flood this exists to prevent. Only easing all the way to
 * `null` re-arms. The mark is per directory, so one skill filling up says
 * nothing about another.
 *
 * Held in memory rather than persisted. A botman restart re-notifies once for a
 * directory still under pressure, which is the right way round: the alternative
 * is a robot that stays silent about a full directory because a previous
 * process had already mentioned it.
 */
/** Pressure as a ladder, so only a climb is worth saying. */
const SEVERITY: Record<CaptureStorageLevel | 'fine', number> = {
  fine: 0,
  warning: 1,
  rotating: 2,
  diskFloor: 3,
};

/**
 * The high-water key the disk floor is tracked under.
 *
 * One key, because free space is a property of the filesystem and every
 * directory shares it. Keying it per directory would say the same thing once
 * per skill. A byte no path can hold keeps it clear of any real directory.
 */
const FILESYSTEM_KEY = '\0filesystem';

export class CaptureStorageReporter {
  private highWaterByDirectory = new Map<string, number>();

  /**
   * Whether this level should be sent now.
   *
   * Records the mark either way, so the caller cannot ask twice and get two
   * answers for one capture.
   */
  public shouldReport(
    directory: string,
    level: CaptureStorageLevel | null,
  ): boolean {
    const severity = SEVERITY[level ?? 'fine'];
    const mark = this.highWaterByDirectory.get(directory) ?? SEVERITY.fine;

    // The mark only climbs. Easing all the way to fine is what clears it, so a
    // directory swinging between the two loud levels is reported once.
    this.highWaterByDirectory.set(
      directory,
      severity === SEVERITY.fine ? SEVERITY.fine : Math.max(mark, severity),
    );

    return severity > mark;
  }

  /**
   * Whether the disk floor is worth saying now.
   *
   * Tracked on the same ladder so it behaves like the rest: raised once when
   * free space first falls short, silent while it stays short, and re-armed
   * once the robot has room again. Reported for the robot rather than for a
   * skill, so the pruning of any directory can be what notices.
   */
  public shouldReportDiskFloor(isBelowFloor: boolean): boolean {
    return this.shouldReport(FILESYSTEM_KEY, isBelowFloor ? 'diskFloor' : null);
  }

  /** Forgets every directory. For tests, and for a runner being torn down. */
  public reset(): void {
    this.highWaterByDirectory.clear();
  }
}

/**
 * The reporter the capture path consults.
 *
 * One per process, because the state it holds is "what has this robot already
 * said", and a per-capture instance would remember nothing and notify on every
 * frame.
 */
export const captureStorageReporter = new CaptureStorageReporter();
